import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import agent from '../agent';
import { isApiError } from '../../common/utils';
import _ from 'lodash';
import axios from 'axios';


export const makeAutoRefresh = createAsyncThunk( "securenetdashboard/makeAutoRefresh",
  async (data, thunkApi) => {
    try {
      const response = await agent.requests.post('/kibana/securenet/auto-refresh', {
          ...data,
      });
      return response;
    } catch (error) {
      if (isApiError(error)) {
        return thunkApi.rejectWithValue(error);
      }

      throw error;
    }
  }
);

export const getAllSecurenetData = createAsyncThunk( "securenetdashboard/getAllSecurenetData",
  async (data, thunkApi) => {
    try {
      const [getSecurityProfileResponse, getThreatActivitiesResponse, getSecurityThreatsResponse, getMostUsedAppsResponse ] = await Promise.all([
        agent.requests.post('/kibana/security-profile', {...data,}).then(r => r),
        agent.requests.post('/kibana/threat-activities', {...data,}).then(r => r),
        agent.requests.post('/kibana/security-threats', {...data,}).then(r => r),
        agent.requests.post('/kibana/most-used-apps', {...data,}).then(r => r),
      ]);
      
      return {getSecurityProfileResponse, getThreatActivitiesResponse, getSecurityThreatsResponse, getMostUsedAppsResponse};

    } catch (error) {
      if (isApiError(error)) {
        return thunkApi.rejectWithValue(error);
      }

      throw error;
    }
  }
);

export const getSecurityProfile = createAsyncThunk( "securenetdashboard/getSecurityProfile",
  async (data, thunkApi) => {
    try {
      const response = await agent.requests.post('/kibana/security-profile', {
          ...data,
      });
      return response;
    } catch (error) {
      if (isApiError(error)) {
        return thunkApi.rejectWithValue(error);
      }

      throw error;
    }
  }
);

export const getThreatActivities = createAsyncThunk( "securenetdashboard/getThreatActivities",
  async (data, thunkApi) => {
    try {
      const response = await agent.requests.post('/kibana/threat-activities', {
          ...data,
      });
      return response;
    } catch (error) {
      if (isApiError(error)) {
        return thunkApi.rejectWithValue(error);
      }

      throw error;
    }
  }
);

export const getSecurityThreats = createAsyncThunk( "securenetdashboard/getSecurityThreats",
  async (data, thunkApi) => {
    try {
      const response = await agent.requests.post('/kibana/security-threats', {
          ...data,
      });
      return response;
    } catch (error) {
      if (isApiError(error)) {
        return thunkApi.rejectWithValue(error);
      }

      throw error;
    }
  }
);

export const getMostUsedApps = createAsyncThunk( "securenetdashboard/getMostUsedApps",
  async (data, thunkApi) => {
    try {
      const response = await agent.requests.post('/kibana/most-used-apps', {
          ...data,
      });
      return response;
    } catch (error) {
      if (isApiError(error)) {
        return thunkApi.rejectWithValue(error);
      }

      throw error;
    }
  }
);

const initialState = {
  status: "idle",
  loading: false,
  error: null,
  makeAutoRefreshResponse: {},
  getSecurityProfileResponse: undefined,
  getThreatActivitiesResponse: undefined,
  getSecurityThreatsResponse: undefined,
  getMostUsedAppsResponse: undefined,
}

const securenetDashboardSlice = createSlice({
  name: 'securenetdashboard',
  initialState,
  reducers: {
    resetStatus: (state) => {
      state.status = "idle"
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    clearSecurenetData: (state) => ({
      ...state,
      getSecurityProfileResponse: undefined,
      getThreatActivitiesResponse: undefined,
      getSecurityThreatsResponse: undefined,
      getMostUsedAppsResponse: undefined,
    })
  },
  extraReducers: (builder) => {

    // makeAutoRefresh
    builder
    .addCase(makeAutoRefresh.pending, (state) => {
      state.status = "makeAutoRefresh/loading";
    })
    .addCase(makeAutoRefresh.fulfilled, (state, action) => {
      state.status = "makeAutoRefresh/success";
      state.makeAutoRefreshResponse = action.payload;
    })
    .addCase(makeAutoRefresh.rejected, (state, action) => {
      state.status = "makeAutoRefresh/failed";
      state.error = action.payload;
    });

    // getAllSecurenetData
    builder
    .addCase(getAllSecurenetData.pending, (state) => ({
      ...state,
      status : "getAllSecurenetData/loading"
    }))
    .addCase(getAllSecurenetData.fulfilled, (state, action) => ({
      ...state,
      status: "getAllSecurenetData/success",
      ...action.payload
    }))
    .addCase(getAllSecurenetData.rejected, (state, action) => ({
      ...state,
      status : "getAllSecurenetData/failed",
      error : action.payload
    }));

    // getSecurityProfile
    builder
    .addCase(getSecurityProfile.pending, (state) => {
      state.status = "getSecurityProfile/loading";
    })
    .addCase(getSecurityProfile.fulfilled, (state, action) => {
      state.status = "getSecurityProfile/success";
      state.getSecurityProfileResponse = action.payload;
    })
    .addCase(getSecurityProfile.rejected, (state, action) => {
      state.status = "getSecurityProfile/failed";
      state.error = action.payload;
    });

    // getThreatActivities
    builder
    .addCase(getThreatActivities.pending, (state) => {
      state.status = "getThreatActivities/loading";
    })
    .addCase(getThreatActivities.fulfilled, (state, action) => {
      state.status = "getThreatActivities/success";
      state.getThreatActivitiesResponse = action.payload;
    })
    .addCase(getThreatActivities.rejected, (state, action) => {
      state.status = "getThreatActivities/failed";
      state.error = action.payload;
    });
    
    // getSecurityThreats
    builder
    .addCase(getSecurityThreats.pending, (state) => {
      state.status = "getSecurityThreats/loading";
    })
    .addCase(getSecurityThreats.fulfilled, (state, action) => {
      state.status = "getSecurityThreats/success";
      state.getSecurityThreatsResponse = action.payload;
    })
    .addCase(getSecurityThreats.rejected, (state, action) => {
      state.status = "getSecurityThreats/failed";
      state.error = action.payload;
    });

    // getMostUsedApps
    builder
    .addCase(getMostUsedApps.pending, (state) => {
      state.status = "getMostUsedApps/loading";
    })
    .addCase(getMostUsedApps.fulfilled, (state, action) => {
      state.status = "getMostUsedApps/success";
      state.getMostUsedAppsResponse = action.payload;
    })
    .addCase(getMostUsedApps.rejected, (state, action) => {
      state.status = "getMostUsedApps/failed";
      state.error = action.payload;
    });

  }
})

export const { resetStatus, clearSecurenetData } = securenetDashboardSlice.actions;

export const loadingSecurenetDashboardStatus = (state) => state.securenetdashboard.status

export const makeAutoRefreshResponse = (state) => state.securenetdashboard.makeAutoRefreshResponse;

export const getSecurityProfileResponse = (state) => state.securenetdashboard.getSecurityProfileResponse;
export const getThreatActivitiesResponse = (state) => state.securenetdashboard.getThreatActivitiesResponse;
export const getSecurityThreatsResponse = (state) => state.securenetdashboard.getSecurityThreatsResponse;
export const getMostUsedAppsResponse = (state) => state.securenetdashboard.getMostUsedAppsResponse;

export const SecurenetDashboardError = (state) => state.securenetdashboard.error 

export default securenetDashboardSlice.reducer;